import type { NodeToggleMetric, NotificationChannelView, ResourcePath } from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { BellRing, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { describeHttpError } from '../api/http';
import { fetchNodeToggle, setNodeToggle } from '../data/node-toggle';
import { ChannelPicker, useNotificationChannels } from './ChannelPicker';
import { Button, Card, Checkbox, Loading, Toasts, useToasts } from './ui';

type RowConfig = {
  metric: NodeToggleMetric;
  checkboxLabel: string;
  hint?: string;
};

/**
 * Los tres avisos de consumo de un nodo, en **una** tarjeta (M15 §4, ampliado a
 * consumo).
 *
 * Eran tres tarjetas apiladas, cada una con su título, su casilla y su lista
 * vertical de canales: en la ficha ocupaban más que todo lo demás junto. Ahora
 * cada aviso es una fila —casilla, pista, y los canales plegados hasta que se
 * necesitan— y la tarjeta lleva un solo título.
 *
 * Por debajo cada fila crea o desactiva una `AlertRule` de ámbito nodo — el mismo
 * motor de histéresis y agrupación del M7, sin que quien lo activa tenga que saber
 * que existe una regla. `support_technician` basta: está acotado a este nodo. Los
 * umbrales no se ajustan desde aquí; quien necesite otros usa la administración
 * general de reglas.
 */
const ROWS: RowConfig[] = [
  { metric: 'node_offline', checkboxLabel: 'Avisarme si este nodo se cae' },
  {
    metric: 'cpu_percent',
    checkboxLabel: 'Avisarme si la CPU se mantiene alta',
    hint: 'Se dispara si la CPU supera 90 % durante 10 minutos seguidos; se apaga bajo 70 %.',
  },
  {
    metric: 'memory_percent',
    checkboxLabel: 'Avisarme si la memoria se mantiene alta',
    hint: 'Se dispara si la memoria supera 85 % durante 5 minutos seguidos; se apaga bajo 75 %.',
  },
];

export function NodeAlertsCard({ nodeId, path }: { nodeId: string; path: ResourcePath }) {
  const channels = useNotificationChannels();
  const toasts = useToasts();

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <BellRing className="size-4 opacity-70" />
          Avisos de este nodo
        </span>
      }
    >
      <Toasts toasts={toasts.toasts} />
      <div className="divide-y divide-black/5 dark:divide-white/5">
        {ROWS.map((row) => (
          <AlertToggleRow
            key={row.metric}
            nodeId={nodeId}
            path={path}
            channels={channels}
            toasts={toasts}
            {...row}
          />
        ))}
      </div>
    </Card>
  );
}

function AlertToggleRow({
  nodeId,
  path,
  channels,
  toasts,
  metric,
  checkboxLabel,
  hint,
}: RowConfig & {
  nodeId: string;
  path: ResourcePath;
  channels: NotificationChannelView[];
  toasts: ReturnType<typeof useToasts>;
}) {
  const { data: permission } = useCan({
    resource: 'node-notify',
    action: 'edit',
    params: { path },
  });

  const [enabled, setEnabled] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const state = await fetchNodeToggle(nodeId, metric);
      setEnabled(state.enabled);
      setSelected(new Set(state.channelIds));
      setDirty(false);
    } catch {
      // Sin permiso o sin backend: se deja apagado y sin canales. El motivo ya
      // lo explica `permission.reason` sobre la propia casilla.
    } finally {
      setLoading(false);
    }
  }, [nodeId, metric]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleChannel = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const result = await setNodeToggle(nodeId, metric, { enabled, channelIds: [...selected] });
      setEnabled(result.enabled);
      setSelected(new Set(result.channelIds));
      setDirty(false);
      toasts.ok(enabled ? 'Aviso activado para este nodo.' : 'Aviso desactivado para este nodo.');
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  // Explica el motivo y apaga los controles en vez de esconder la fila —
  // mismo criterio que el resto del panel (ver el comentario de `App.tsx`):
  // quien decide de verdad es el backend, y ocultar la sección le escondería a
  // un `read_only` que la vigilancia existe, no solo que no puede tocarla.
  const canEdit = permission?.can ?? false;
  const reason = !canEdit ? (permission?.reason ?? 'Sin permiso') : undefined;

  if (loading) {
    return (
      <div className="py-2.5 first:pt-0 last:pb-0">
        <Loading label="Cargando…" />
      </div>
    );
  }

  return (
    <div className="grid gap-2 py-2.5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Checkbox
          label={checkboxLabel}
          checked={enabled}
          disabled={!canEdit}
          title={reason}
          onChange={(event) => {
            setEnabled(event.target.checked);
            setDirty(true);
          }}
        />
        <div className="flex items-center gap-1.5">
          {enabled && (
            <Button variant="ghost" size="sm" onClick={() => setOpen((value) => !value)}>
              Canales ({selected.size})
              <ChevronDown
                className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </Button>
          )}
          {dirty && (
            <Button
              variant="primary"
              size="sm"
              disabled={!canEdit || busy}
              onClick={() => void save()}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>

      {hint && <span className="text-xs opacity-60">{hint}</span>}
      {reason && <span className="text-xs opacity-60">{reason}</span>}

      {enabled && open && (
        <ChannelPicker
          channels={channels}
          selected={selected}
          disabled={!canEdit}
          onToggle={toggleChannel}
        />
      )}
    </div>
  );
}
